<template>
  <div class="text-center login">
    <v-container fluid>
      <v-card
        class="text-center pa-3 mt-4"
        style="max-width: 400px; margin-left: auto; margin-right: auto;"
      >
        <template v-if="!emailConfirmed && twoFactorAuthenticationEnabled">
          <div class="title mb-3" v-html="$t('login.email_enter_info')" />
          <v-text-field
            type="email"
            name="email"
            placeholder="E-mail"
            filled
            v-model="email"
            :hide-details="true"
            @keyup.enter="send"
            class="mb-2"
          />
        </template>
        <template v-else>
          <div
            v-if="twoFactorAuthenticationEnabled"
            class="title mb-3"
            v-html="$t('login.sms_pin_info', { phone })"
          />
          <div v-else class="title mb-3">
            {{ $t('login.login_info') }}
          </div>
          <v-text-field
            type="number"
            placeholder="******"
            :label="$t('login.enter_pin')"
            filled
            v-model="pin"
            :hide-details="true"
            @keyup.enter="send"
            class="mb-2"
          />
        </template>
        <v-alert
          type="error"
          class="text-left"
          :value="error != null"
          v-html="error"
        />
        <v-alert
          type="info"
          class="text-left"
          :value="info != null"
          v-html="$t('login.logout_another_device')"
        />
        <v-btn color="primary" @click="send" :disabled="working">
          <i v-if="working" class="fal fa-spinner fa-spin fa-fw" />
          {{ $t('login.send_login') }}
        </v-btn>
      </v-card>
    </v-container>
  </div>
</template>

<script>
import { mapMutations, mapState, mapGetters } from 'vuex'
import LogoMixin from './mixins/logo'

export default {
  mixins: [mixins, LogoMixin],

  props: ['parent'],

  data() {
    const logoutReason = sessionStorage.getItem('logout_reason')
    let info = null

    if (logoutReason) {
      sessionStorage.removeItem('logout_reason')
      info = true
    }

    return {
      email: '',
      phone: '',
      pin: '',
      info,
      error: null,
      working: false,
      emailConfirmed: false,
    }
  },

  computed: {
    ...mapState({
      twoFactorAuthenticationEnabled: state =>
        state.auth.twoFactorAuthenticationEnabled,
      contactInfo: state => state.common.contactInfo,
      deviceId: state => state.common.deviceId,
      redirectUrl: state => state.auth.redirectUrl,
    }),

    ...mapGetters('common', ['isVirtual']),
  },

  created() {
    this.changeNavbar()

    if (!this.isVirtual) {
      this.checkLogoHeight('/images/front/top-img.jpg')
    }
  },

  mounted() {
    if (EasyEvent.fromAutologin) {
      if (!EasyEvent.autologin_success) {
        this.setError(this.$t('login.autologin_failed'))
      } else if (this.twoFactorAuthenticationEnabled) {
        if (!EasyEvent.two_fa_phone) {
          this.setError(this.$t('login.phone_not_set'))
        } else {
          this.emailConfirmed = true
          this.phone = EasyEvent.two_fa_phone
        }
      }

      EasyEvent.fromAutologin = false
    }
  },

  methods: {
    ...mapMutations('auth', ['setUser']),

    send() {
      if (!this.emailConfirmed && this.twoFactorAuthenticationEnabled) {
        this.requestSMS()
      } else {
        this.sendLogin()
      }
    },

    requestSMS() {
      if (this.email === '') {
        return
      }

      this.working = true
      this.setError(null)
      this.info = null

      axios
        .post('/login/sms', { email: this.email })
        .then(response => {
          this.phone = response.data.phone
          this.emailConfirmed = true
          this.working = false
        })
        .catch(error => {
          if (error.response && error.response.status === 404) {
            this.setError(this.$t('login.email_not_found'))
          } else if (error.response && error.response.status === 422) {
            this.setError(this.$t('login.phone_not_set'))
          } else if (error.response && error.response.status === 429) {
            this.setError(this.$t('login.too_many_wrong_login'))
          } else {
            this.setError(this.$t('common.communicationError'))
          }

          this.working = false
        })
    },

    sendLogin() {
      if (this.pin === '') {
        return
      }

      this.working = true
      this.setError(null)
      this.info = null
      const subscription = JSON.parse(localStorage.getItem('subscription'))
      const endpoint = subscription ? subscription.endpoint : null

      const loginData = {
        pin: this.pin,
        device_id: this.deviceId,
        endpoint,
      }

      axios
        .post('/login', loginData)
        .then(response => {
          const visitor = response.data.visitor

          this.setUser({ user: visitor, fromLogin: true })

          if (this.isVirtual) {
            window.location =
              this.$route.path === '/login' ? '/' : `/#${this.$route.path}`
          } else {
            if (this.redirectUrl) {
              window.location = `/#${this.redirectUrl}`
            } else {
              window.location = '/#'
            }

            this.working = false

            location.reload()
          }
        })
        .catch(error => {
          if (error.response && error.response.status === 422) {
            this.setError(this.$t('login.wrong_pin'))
          } else if (error.response && error.response.status === 429) {
            this.setError(this.$t('login.too_many_wrong_login'))
          } else {
            this.setError(this.$t('common.communicationError'))
          }
          this.working = false
        })
    },

    setError(error) {
      if (!error) {
        this.error = null
        return
      }

      const { email, phone } = this.contactInfo
      if (email && phone) {
        this.error = `${error}<br>${this.$t('login.support.contact_both', { email, phone })}`
      } else if (email) {
        this.error = `${error}<br>${this.$t('login.support.contact_email', { email })}`
      } else if (phone) {
        this.error = `${error}<br>${this.$t('login.support.contact_phone', { phone })}`
      } else {
        this.error = error
      }
    },
  },
}
</script>
